import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { Download, AlertTriangle, CheckCircle2, ArrowLeft, FileSpreadsheet } from 'lucide-react';

const FORMATS = [
  { value: 'GENERIC_CSV_V2', label: 'Generic CSV v2', desc: 'Full claim line details' },
  { value: 'NDIS_READY_CSV_V1', label: 'NDIS-Ready CSV', desc: 'PRODA-ready export with NDIS validation' },
  { value: 'INVOICE_SUPPORT_EXPORT_V1', label: 'Invoice Support Export', desc: 'Support items with component breakdown' },
];

export default function ClaimExportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [format, setFormat] = useState('GENERIC_CSV_V2');
  const [supportItems, setSupportItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [claim, setClaim] = useState(null);

  const loadSupportItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/claims/${id}/export-support-items`);
      setSupportItems(data.data);
      setClaim({ claimNo: data.data.claimNo, status: data.data.status, totalAmount: data.data.totalAmount });
    } catch { notify.error(e, 'CLAIM_LOAD_FAILED'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadSupportItems(); }, [loadSupportItems]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/claims/${id}/export-csv?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${claim?.claimNo || 'claim'}_${format}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      if (e.response?.status === 422) {
        const text = await e.response.data.text();
        const err = JSON.parse(text);
        notify.error(err, 'CLAIM_VALIDATION_FAILED');
      } else {
        notify.error(err, 'CLAIM_EXPORT_FAILED');
      }
    } finally { setExporting(false); }
  };

  const errors = supportItems?.validationErrors || [];

  return (
    <div className="space-y-6" data-testid="claim-export-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-2xl font-bold tracking-tight">Export Claim {claim?.claimNo}</h1>
        {claim && <Badge variant="outline">{claim.status}</Badge>}
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <>
          {errors.length > 0 && (
            <Card className="border-amber-500">
              <CardHeader className="pb-2"><CardTitle className="text-amber-600 flex items-center gap-2 text-base"><AlertTriangle className="w-5 h-5" />Validation Issues ({errors.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {errors.map((e, i) => (
                    <p key={i} className="text-sm text-amber-700">{e.participantName || e.participantId}: {e.message}</p>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">NDIS-Ready export will fail until these are resolved.</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" />Export Format</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMATS.map(f => (
                    <SelectItem key={f.value} value={f.value}>
                      <div><span className="font-medium">{f.label}</span><span className="text-xs text-muted-foreground ml-2">— {f.desc}</span></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {supportItems && <span>{supportItems.totalItems} line items · ${supportItems.totalAmount?.toFixed(2)} total</span>}
                </div>
                <Button onClick={handleExport} disabled={exporting} data-testid="download-export-btn">
                  <Download className="w-4 h-4 mr-2" />{exporting ? 'Exporting...' : 'Download CSV'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {supportItems?.items?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Preview ({supportItems.items.length} items)</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b">
                      <th className="text-left py-2 pr-3">Participant</th>
                      <th className="text-left py-2 pr-3">NDIS #</th>
                      <th className="text-left py-2 pr-3">Service</th>
                      <th className="text-left py-2 pr-3">Date</th>
                      <th className="text-right py-2 pr-3">Qty</th>
                      <th className="text-right py-2 pr-3">Rate</th>
                      <th className="text-right py-2">Total</th>
                    </tr></thead>
                    <tbody>
                      {supportItems.items.map((item, i) => (
                        <tr key={i} className="border-b border-dashed">
                          <td className="py-2 pr-3">{item.participantName}</td>
                          <td className="py-2 pr-3">{item.ndisNumber || <span className="text-amber-500">Missing</span>}</td>
                          <td className="py-2 pr-3">{item.supportItemName}</td>
                          <td className="py-2 pr-3">{item.serviceDate}</td>
                          <td className="py-2 pr-3 text-right">{item.quantity}</td>
                          <td className="py-2 pr-3 text-right">${item.unitPrice?.toFixed(2)}</td>
                          <td className="py-2 text-right font-medium">${item.total?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
